/**
 * «Compartir con mi equipo» (docs/29 §5): avisa a compañeros del Team con un enlace al documento
 * (el documento no se copia; el enlace respeta los permisos de la app). Les llega a su bandeja de
 * Mensajes y, si lo pidieron, por WhatsApp o correo.
 */
import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  MessageBar,
  MessageBarBody,
  Spinner,
  Textarea,
} from '@fluentui/react-components';
import {
  compartirConEquipo,
  obtenerEquipo,
  textoDeError,
  type AppOrigen,
  type MiembroDelEquipo,
} from '../../api/mensajes.js';
import { useAuthStore } from '../../stores/authStore.js';

export interface CompartirConEquipoProps {
  abierto: boolean;
  onCerrar: () => void;
  titulo: string;
  url: string;
  app: AppOrigen;
  refDoc?: string;
}

export function CompartirConEquipoDialog({ abierto, onCerrar, titulo, url, app, refDoc }: CompartirConEquipoProps): JSX.Element {
  const miCorreo = useAuthStore((s) => s.user?.email ?? '').toLowerCase();
  const [miembros, setMiembros] = useState<MiembroDelEquipo[] | null>(null);
  const [todos, setTodos] = useState(true);
  const [elegidos, setElegidos] = useState<Set<string>>(new Set());
  const [nota, setNota] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState<string | null>(null);

  useEffect(() => {
    if (!abierto) return;
    let vivo = true;
    setMiembros(null);
    setTodos(true);
    setElegidos(new Set());
    setNota('');
    setError(null);
    setListo(null);
    obtenerEquipo()
      .then((m) => vivo && setMiembros(m.filter((x) => x.correo.toLowerCase() !== miCorreo)))
      .catch((e) => {
        if (!vivo) return;
        setMiembros([]);
        setError(textoDeError(e));
      });
    return () => {
      vivo = false;
    };
  }, [abierto, miCorreo]);

  const alternar = (correo: string, si: boolean) =>
    setElegidos((s) => {
      const n = new Set(s);
      if (si) n.add(correo);
      else n.delete(correo);
      return n;
    });

  async function compartir() {
    if (!todos && elegidos.size === 0) {
      setError('Elige al menos a un compañero o marca «Todo el equipo».');
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const r = await compartirConEquipo({
        miembros: todos ? 'todos' : [...elegidos],
        titulo,
        url,
        app,
        ...(refDoc ? { ref: refDoc } : {}),
        ...(nota.trim() ? { nota: nota.trim() } : {}),
      });
      setListo(r.avisados === 1 ? 'Listo: avisamos a 1 compañero.' : `Listo: avisamos a ${r.avisados} compañeros.`);
    } catch (e) {
      setError(textoDeError(e));
    } finally {
      setEnviando(false);
    }
  }

  const sinEquipo = miembros !== null && miembros.length === 0 && !error;

  return (
    <Dialog open={abierto} onOpenChange={(_, d) => !d.open && onCerrar()}>
      <DialogSurface style={{ maxWidth: 520 }}>
        <DialogBody>
          <DialogTitle>Compartir con mi equipo</DialogTitle>
          <DialogContent>
            {listo ? (
              <MessageBar intent="success">
                <MessageBarBody>{listo}</MessageBarBody>
              </MessageBar>
            ) : (
              <div style={{ display: 'grid', rowGap: 12 }}>
                <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                  Tus compañeros recibirán un aviso en Mensajes con el enlace a <b>{titulo}</b>. Lo abren con su propia
                  cuenta: solo lo ve quien tiene permiso.
                </p>
                {miembros === null ? (
                  <Spinner size="tiny" label="Cargando tu equipo…" />
                ) : sinEquipo ? (
                  <p style={{ margin: 0 }}>Todavía no tienes compañeros en tu equipo.</p>
                ) : (
                  miembros.length > 0 && (
                    <Field label="¿A quién?">
                      <Checkbox checked={todos} onChange={(_, d) => setTodos(Boolean(d.checked))} label="Todo el equipo" />
                      {!todos && (
                        <div style={{ display: 'grid', maxHeight: 220, overflowY: 'auto', paddingLeft: 8 }}>
                          {miembros.map((m) => (
                            <Checkbox
                              key={m.correo}
                              checked={elegidos.has(m.correo)}
                              onChange={(_, d) => alternar(m.correo, Boolean(d.checked))}
                              label={m.nombre ? `${m.nombre} · ${m.correo}` : m.correo}
                            />
                          ))}
                        </div>
                      )}
                    </Field>
                  )
                )}
                <Field label="Nota (opcional)">
                  <Textarea rows={3} value={nota} maxLength={500} onChange={(_, d) => setNota(d.value)} />
                </Field>
                {error && (
                  <MessageBar intent="error">
                    <MessageBarBody>{error}</MessageBarBody>
                  </MessageBar>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCerrar}>
              {listo ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!listo && (
              <Button
                appearance="primary"
                disabled={enviando || !miembros || miembros.length === 0}
                onClick={() => void compartir()}
              >
                {enviando ? 'Compartiendo…' : 'Compartir'}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
